import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Checkbox, CircularProgress,
    Snackbar, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import { motion } from 'framer-motion'; 
import { BASE_URL } from '../../utils/endpoints';


const truncateText = (text, maxLength = 70) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
};


const containerVariants = {
    hidden: { opacity: 0, y: 20 }, 
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5, 
            ease: "easeOut", 
            when: "beforeChildren", 
        }
    }
};

const BrandFormList = () => {
    
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    const [brandForms, setBrandForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalRows, setTotalRows] = useState(0);
    const [selectedRows, setSelectedRows] = useState([]); 

    
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    
    const [itemsToDelete, setItemsToDelete] = useState([]);
    
    const [isBulkDelete, setIsBulkDelete] = useState(false);

    
    const fetchBrandForms = async () => {
        try {
            setLoading(true); 
            setError(null); 
            
            const response = await fetch(`${BASE_URL}/brand-forms?page=${page + 1}&limit=${rowsPerPage}`);
            if (!response.ok) {
                
                throw new Error('Failed to fetch brand forms');
            }
            const data = await response.json();

            
            const formattedBrandForms = (data.forms || []).map(form => ({
                id: form._id || `fallback-${Math.random()}`, 
                brandName: form.brand_name || 'Unnamed Brand',
                category: form.category || 'Unknown Category',
                email: form.email || 'N/A',
                phoneNumber: form.phone_number || 'N/A',
                website: form.website_url || '#',
            }));

            setBrandForms(formattedBrandForms); 
            setTotalRows(data.total || formattedBrandForms.length); 
        } catch (err) {
            setError('Failed to fetch brand forms'); 
            setSnackbar({ open: true, message: 'Failed to fetch brand forms.', severity: 'error' }); 
        } finally {
            setLoading(false); 
        }
    };

    
    useEffect(() => {
        fetchBrandForms();
    }, [page, rowsPerPage]); 

    
    const handleSingleDelete = (id) => {
        setIsBulkDelete(false); 
        setItemsToDelete([id]); 
        setOpenDeleteDialog(true); 
    };

    
    const handleBulkDelete = () => {
        if (selectedRows.length === 0) return; 
        setIsBulkDelete(true); 
        setItemsToDelete(selectedRows); 
        setOpenDeleteDialog(true); 
    };

    
    const handleDeleteConfirmed = async () => {
        setOpenDeleteDialog(false); 

        try {
            setLoading(true); 
            
            const deletePromises = itemsToDelete.map(id =>
                fetch(`${BASE_URL}/brand-forms/${id}`, {
                    method: 'DELETE',
                })
            );
            const responses = await Promise.all(deletePromises); 
            const allSuccessful = responses.every(response => response.ok);

            if (allSuccessful) {
                
                setBrandForms(prevForms => prevForms.filter(form => !itemsToDelete.includes(form.id)));
                setSelectedRows([]); 
                setSnackbar({ open: true, message: 'Brand form(s) deleted successfully.', severity: 'success' }); 
                
                fetchBrandForms();
            } else {
                setError('Failed to delete some brand forms'); 
                setSnackbar({ open: true, message: 'Failed to delete some brand forms.', severity: 'error' }); 
            }
        } catch (error) {
            console.error('Error deleting brand forms:', error);
            setError('Error occurred while deleting brand forms'); 
            setSnackbar({ open: true, message: 'Error occurred while deleting brand forms.', severity: 'error' }); 
        } finally {
            setLoading(false); 
            setItemsToDelete([]); 
            setIsBulkDelete(false); 
        }
    };

    
    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false); 
        setItemsToDelete([]); 
        setIsBulkDelete(false); 
    };

    
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    
    const columns = [
        {
            field: 'select',
            headerName: '',
            width: 70,
            sortable: false, 
            filterable: false, 
            renderCell: (params) => (
                <Checkbox
                    checked={selectedRows.includes(params.row.id)}
                    onChange={(event) => {
                        setSelectedRows(
                            event.target.checked
                                ? [...selectedRows, params.row.id]
                                : selectedRows.filter(id => id !== params.row.id)
                        );
                    }}
                    sx={{
                        color: '#2e7d32',
                        '&.Mui-checked': {
                            color: '#2e7d32',
                        },
                    }}
                />
            ),
        },
        {
            field: 'brandName',
            headerName: <Typography variant="subtitle1" fontWeight="bold">Brand Name</Typography>,
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 1,
                    p: 1,
                    bgcolor: '#f5f5f5',
                    height: '100%',
                    width: '100%',

                }}>
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: '#2e7d32', fontSize: '16px' }}
                    >
                        {truncateText(params.row.brandName)}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'category',
            headerName: <Typography variant="subtitle1" fontWeight="bold">Category</Typography>,
            width: 180,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                    }}
                >
                    {truncateText(params.row.category)}
                </Typography>
            ),
        },
        {
            field: 'email',
            headerName: <Typography variant="subtitle1" fontWeight="bold">Email</Typography>,
            width: 220,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                    }}
                >
                    {truncateText(params.row.email)}
                </Typography>
            ),
        },
        {
            field: 'phoneNumber',
            headerName: <Typography variant="subtitle1" fontWeight="bold">Phone Number</Typography>,
            width: 180,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                    }}
                >
                    {truncateText(params.row.phoneNumber)}
                </Typography>
            ),
        },
        {
            field: 'website',
            headerName: <Typography variant="subtitle1" fontWeight="bold"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                }}>Website</Typography>,
            width: 250,
            renderCell: (params) => (
                <motion.a
                    href={params.row.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02, color: '#1a73e8' }}
                    whileTap={{ scale: 0.98 }}
                    style={{ textDecoration: 'none', color: '#1976d2' }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {truncateText(params.row.website)}
                    </Typography>
                </motion.a>
            ),
        },
        {
            field: 'actions',
            headerName: <Typography variant="subtitle1" fontWeight="bold">Actions</Typography>,
            sortable: false,
            filterable: false,
            width: 100,
            renderCell: (params) => (
                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleSingleDelete(params.row.id)}
                    sx={{ borderRadius: 1.5 }}
                    component={motion.button} 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    Delete
                </Button>
            ),
        },
    ];

    
    if (loading && brandForms.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress sx={{ color: '#2e7d32' }} />
                <Typography variant="h6" ml={2}>Loading brand forms...</Typography>
            </Box>
        );
    }

    if (error && brandForms.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
                marginTop: '16px',
                borderRadius: '8px',
                padding: '24px',
                backgroundColor: '#fff',
                boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                fontFamily: '"Inter", sans-serif'
            }}
        >
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.0 }}
            >
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                    Brand Forms
                </Typography>
                <Typography variant="body2" color="#2e7d32" sx={{ mb: 2 }}>
                    Dashboard • Brand Forms • List
                </Typography>
            </motion.div>

            {/* Top Bar: Search and Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        placeholder="Search..."
                        size="small"
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ marginRight: 1, color: '#9ca3af' }} />, 
                        }}
                    
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        sx={{
                            borderColor: '#9ca3af',
                            color: '#9ca3af',
                            '&:hover': { borderColor: 'tomato', color: 'red' },
                            borderRadius: 1.5 
                        }}
                        startIcon={<DeleteIcon />}
                        onClick={handleBulkDelete}
                        disabled={selectedRows.length === 0}
                        component={motion.button} 
                        whileHover={{ scale: 1.05 }} 
                        whileTap={{ scale: 0.95 }} 
                    >
                        Delete Selected ({selectedRows.length})
                    </Button>
                </Box>
            </Box>

            {/* DataGrid */}
            <DataGrid
                rows={brandForms}
                columns={columns}
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                    pagination: {
                        paginationModel: { page: page, pageSize: rowsPerPage },
                    },
                }}
                disableRowSelectionOnClick 
                autoHeight 
                rowHeight={60} 
                getRowId={(row) => row.id} 
                pagination
                paginationMode="server" 
                rowCount={totalRows} 
                onPaginationModelChange={(newModel) => {
                    setPage(newModel.page);
                    setRowsPerPage(newModel.pageSize);
                    setSelectedRows([]); 
                }}
                sx={{
                    borderRadius: 2, 
                    boxShadow: 3, 
                    '& .MuiDataGrid-columnHeaders': {
                        bgcolor: '#f9fafb', 
                        fontWeight: 'bold', 
                    },
                    
                    '& .MuiDataGrid-row:nth-of-type(odd)': {
                        backgroundColor: '#fdfdfd',
                    },
                    '& .MuiDataGrid-row:hover': {
                        backgroundColor: '#f5f5f5',
                    },
                }}
            />

            {/* Snackbar for Notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 1.5, boxShadow: 6 }} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={handleCloseDeleteDialog}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle id="delete-dialog-title" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                    {"Confirm Deletion"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        {isBulkDelete
                            ? `Are you sure you want to delete ${itemsToDelete.length} selected brand form(s)? This action cannot be undone.`
                            : `Are you sure you want to delete this brand form? This action cannot be undone.`
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} sx={{ color: 'text.secondary' }}>Cancel</Button>
                    <Button onClick={handleDeleteConfirmed} autoFocus variant="contained" color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </motion.div>
    );
};

export default BrandFormList;
